import React, { useState, ChangeEvent, useRef, useCallback, useMemo } from 'react';
import isEqual from 'lodash-es/isEqual';
import {
  ChannelFilterSelectionFragment,
  FilterSelectionType,
  useCreateChannelFilterSelectionMutation,
  useGetChannelFilterSelectionsQuery,
} from 'shared/types/graphql';
import { IChannelListFilterSelection } from 'pages/Conversations/interfaces/IChannelListFilterSelection';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import {
  Switch,
  UnorderedList,
  ListItem,
  Flex,
  VStack,
  HStack,
  Text,
  TextInput,
  ModalCloseButton,
  ModalContent,
  Icon,
  Alert,
  ModalHeader,
  ModalBody,
  ModalFooterButtons,
} from '@televet/kibble-ui';

interface ISaveChannelListFilterModalContent {
  currentChannelFilterSelection: IChannelListFilterSelection;
  onSuccess: (channelFilterSelection: ChannelFilterSelectionFragment) => void;
  onCancel: () => void;
}

const SaveChannelListFilterModalContent = ({
  currentChannelFilterSelection,
  onSuccess,
  onCancel,
}: ISaveChannelListFilterModalContent): JSX.Element => {
  const [filterName, setFilterName] = useState('');
  const [isFilterShared, setIsFilterShared] = useState(true);
  const [filterNameValidationMessage, setFilterNameValidationMessage] = useState('');
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  const { data: channelFilterSelectionsData } = useGetChannelFilterSelectionsQuery({
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });
  const filterMatches = useMemo(() => {
    const currentFilterIds = new Set(
      Object.values(currentChannelFilterSelection.filters)
        .flat()
        .filter((value) => typeof value === 'string'),
    );
    return channelFilterSelectionsData?.savedChannelFilterSelections.filter((selection) => {
      const savedFilterIds = new Set(
        Object.values(selection.filters)
          .flat()
          .filter((value) => typeof value === 'string'),
      );
      return isEqual(savedFilterIds, currentFilterIds);
    });
  }, [channelFilterSelectionsData, currentChannelFilterSelection]);

  const [createChannelFilterSelection, { loading: isCreatingChannelFilterSelection }] =
    useCreateChannelFilterSelectionMutation({
      refetchQueries: ['getChannelFilterSelections'],
      awaitRefetchQueries: true,
    });

  const handleSaveFilterClick = useCallback(async (): Promise<void> => {
    if (!filterName) {
      setFilterNameValidationMessage('Please enter a name for your filter.');
      nameInputRef.current?.focus();
      return;
    }
    const { filters } = currentChannelFilterSelection;
    const result = await createChannelFilterSelection({
      variables: {
        data: {
          name: filterName,
          filters,
          filterSelectionType: isFilterShared ? FilterSelectionType.PublicReadonly : FilterSelectionType.Private,
        },
      },
    });
    if (result.data?.createChannelFilterSelection) {
      onSuccess(result.data.createChannelFilterSelection);
    }
  }, [currentChannelFilterSelection, filterName, isFilterShared, createChannelFilterSelection, onSuccess]);

  return (
    <ModalContent>
      <ModalHeader>Save New Filter</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <VStack spacing="3" align="stretch">
          <Flex align="center" justify="space-between">
            <HStack flex="1 1 auto">
              <TextInput
                isRequired
                isInvalid={!!filterNameValidationMessage}
                label="Filter Name"
                labelStyle={{ fontWeight: 'semibold', color: 'text.default' }}
                autoFocus
                placeholder="My Filter"
                ref={nameInputRef}
                onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                  if (e.target.value) {
                    setFilterNameValidationMessage('');
                  }
                  setFilterName(e.target.value);
                }}
                errorText={filterNameValidationMessage}
              />
            </HStack>
          </Flex>
          <VStack align="flex-start" py="3">
            <HStack>
              <Icon name="team" />
              <Text fontWeight="semibold" ml="2">
                Share with team?
              </Text>
              <Switch
                id="channel-list-filter-edit-share"
                isChecked={isFilterShared}
                onChange={(): void => {
                  setIsFilterShared((value) => !value);
                }}
                formControlStyle={{ width: 'auto' }}
              />
            </HStack>

            <Text variant="subtle">
              Sharing will make this filter visible to the rest of your team under Saved Filters.
            </Text>
          </VStack>
          {!!filterMatches?.length && (
            <Alert
              status="warning"
              title="The following Saved Filters match your current filter selections:"
              hideCloseButton
              description={
                <UnorderedList mt="1" stylePosition="inside">
                  {filterMatches?.map((filter) => (
                    <ListItem key={filter.id} fontWeight="bold">
                      {filter.name}
                    </ListItem>
                  ))}
                </UnorderedList>
              }
            />
          )}
        </VStack>
      </ModalBody>
      <ModalFooterButtons
        onCancel={onCancel}
        submitText="Save Filter"
        onSubmit={handleSaveFilterClick}
        isLoading={isCreatingChannelFilterSelection}
      />
    </ModalContent>
  );
};

export default SaveChannelListFilterModalContent;
